import React from 'react';
import { PresentationChart } from '@phosphor-icons/react';
import { useViewerStore } from '../../../../store/store';

function loanMonthly(principal: number, annualRate: number, termYears: number): number {
    if (principal <= 0) return 0;
    const r = annualRate / 12;
    const n = termYears * 12;
    if (r === 0) return principal / n;
    return principal * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

/**
 * Proposal summary card for oneDRAW v2 overlay.
 * Shows system cost, tax credit, net cost, financing, monthly savings, PPW.
 * Reference: SiteDashboard ProposalSummary.
 * No mock data — reads directly from store.
 */
export const ProposalSummaryCard: React.FC = () => {
    const ppwValue = useViewerStore((s) => s.ppwValue);
    const totalSystemSize = useViewerStore((s) => s.totalSystemSize);
    const actualWattage = useViewerStore((s) => s.actualWattage);
    // const productionCalculated = useViewerStore((s) => s.productionCalculated);
    // const appliedProposal = useViewerStore((s) => s.appliedAIDesignProposal);

    const hasData = totalSystemSize > 0 && ppwValue > 0; // && (!!appliedProposal || productionCalculated);

    const systemCost = hasData ? totalSystemSize * 1000 * ppwValue : 0;
    const taxCredit = systemCost * 0.3;
    const netCost = systemCost - taxCredit;
    const emi = hasData ? loanMonthly(systemCost, 0.049, 25) : 0;
    const monthlySavings = hasData && actualWattage > 0 ? (actualWattage * 0.16) / 12 : 0;

    if (!hasData) return null;

    const items = [
        { label: 'System Cost', value: `$${Math.round(systemCost).toLocaleString()}` },
        { label: 'Est. Tax Credit (30%)', value: `-$${Math.round(taxCredit).toLocaleString()}`, green: true },
        { label: 'Net Cost', value: `$${Math.round(netCost).toLocaleString()}`, bold: true },
        { label: 'Financing', value: `$${Math.round(emi)}/mo · 25yr · 4.9% APR` },
        { label: 'Avg. Monthly Savings', value: monthlySavings > 0 ? `$${Math.round(monthlySavings)}/mo` : '', green: true },
        { label: 'Price per Watt', value: `$${ppwValue.toFixed(2)}/W` },
    ];

    return (
        <div className="obd-card obd-card--proposal">
            <span className="obd-card__label obd-card__label--section-icon">
                <span className="obd-section-head-icon obd-section-head-icon--inline" aria-hidden>
                    <PresentationChart size={16} weight="duotone" />
                </span>
                Proposal Summary
            </span>
            <div className="obd-proposal__grid">
                {items.map((item) => (
                    <div key={item.label} className="obd-proposal__item">
                        <span className="obd-proposal__item-label">{item.label}</span>
                        <span className={`obd-proposal__item-value ${item.green ? 'obd-proposal__item-value--green' : ''} ${item.bold ? 'obd-proposal__item-value--bold' : ''}`}>
                            {item.value}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ProposalSummaryCard;
